import {
  Container,
  ContainerOptions,
  Graphics,
  ObservablePoint,
} from "pixi.js";
import gsap from "gsap";
import { Digit } from "./Digit";
import { StaticSymbol } from "./StaticSymbol";

export interface OdometerOptions extends ContainerOptions {
  digitCount: number;
  fontFamily: string;
  fontSize: number;
  tint?: number;
  spacing?: number;
  initialValue?: number;
  extraSpins?: number;
  formatter?: (value: number) => string;
  maxWidth?: number;
  maxHeight?: number;
}

export class Odometer extends Container {
  private readonly digits: Digit[] = [];
  private readonly symbols: StaticSymbol[] = [];

  private readonly axleProxy: { pos: number } = { pos: 0 };

  private axleTween: gsap.core.Tween | null = null;

  private readonly fractionDigitCount: number;
  private readonly formatter: (value: number) => string;

  private readonly _digitCount: number;
  private readonly _extraSpins: number;
  private readonly _spacing: number;
  private readonly _maxWidth?: number;
  private readonly _maxHeight?: number;
  private _contentAnchor: ObservablePoint;
  private _currentValue: number = 0;
  private readonly formattedExample: string;
  private lastDigits: number[] = [];
  private contentContainer: Container;

  // private dbgGfx: Graphics = new Graphics();

  constructor(options: OdometerOptions) {
    super(options);

    // this.addChild(this.dbgGfx);
    // this.dbgGfx.zIndex = 99999;

    // Initialize content anchor first to prevent undefined errors
    this._contentAnchor = new ObservablePoint(this, 0, 0);

    this._digitCount = options.digitCount;
    this._extraSpins = options.extraSpins ?? 1;
    this._spacing = options.spacing ?? 0;
    this._maxWidth = options.maxWidth;
    this._maxHeight = options.maxHeight;
    this._currentValue = options.initialValue ?? 0;
    this.formatter = options.formatter ?? ((value: number) => value.toString());

    // PIXI handles the anchor property, we use our own content anchor for internal positioning

    // Create a content container for all digits and symbols
    this.contentContainer = new Container();
    this.addChild(this.contentContainer);

    // Generate an example formatted number to determine the structure
    const exampleValue = Math.pow(10, this._digitCount) - 1;
    this.formattedExample = this.formatter(exampleValue);

    // Calculate the number of decimal places in the formatted example
    const decimalMatch = this.formattedExample.match(/\.(\d+)/);
    this.fractionDigitCount = decimalMatch ? decimalMatch[1].length : 0;

    // Create digits and symbols based on the formatted example
    let i = 0;
    while (i < this.formattedExample.length) {
      const char = this.formattedExample[i];
      if (/\d/.test(char)) {
        // Create a digit for numeric characters
        const digit = new Digit({
          fontFamily: options.fontFamily,
          fontSize: options.fontSize,
          tint: options.tint,
        });

        this.digits.push(digit);
        this.contentContainer.addChild(digit);
        i++;
      } else {
        // Group sequential non-numeric characters into a single symbol
        let symbolText = char;
        let j = i + 1;
        while (
          j < this.formattedExample.length &&
          !/\d/.test(this.formattedExample[j])
        ) {
          symbolText += this.formattedExample[j];
          j++;
        }

        // Create a static symbol for the grouped non-numeric characters
        const symbol = new StaticSymbol({
          fontFamily: options.fontFamily,
          fontSize: options.fontSize,
          tint: options.tint,
          char: symbolText,
        });

        this.symbols.push(symbol);
        this.contentContainer.addChild(symbol);
        i = j; // Skip the processed characters
      }

      // const dbgGfx = new Graphics();
      // dbgGfx.rect(-1, -20, 2, 40).fill({ color: 0xff0000 });
      // dbgGfx.rect(-20, -1, 40, 2).fill({ color: 0xff0000 });
      // dbgGfx.zIndex = 9999;
      // this.addChild(dbgGfx);

      // this.dbgGfx = new Graphics();
      // this.addChild(this.dbgGfx);
    }

    // Reverse digits array so index 0 is the least significant digit (rightmost)
    this.digits.reverse();

    // Position elements using the repositionElements method
    this.repositionElements();

    this.applySizeConstraints();

    // this.lastDigits = new Array(this.digits.length).fill(0);

    this.rotateTo(this._currentValue, { duration: 0, easing: "none" });
  }

  public getValue = (): number => {
    return this.axleProxy.pos;
  };

  private determineVisibility(value: number): void {
    const formattedValue = this.formatter(value);

    // Extract digits from the formatted value
    const formattedDigits = formattedValue
      .split("")
      .filter((char) => /\d/.test(char));

    // Calculate how many digit positions in the odometer are NOT used by this value
    // The odometer has a fixed structure, but smaller values don't use all positions
    const unusedDigitPositions = this.digits.length - formattedDigits.length;

    // Now apply visibility based on the odometer's digit structure
    // The odometer's digits array is reversed (index 0 = least significant)
    // We need to hide the most significant digits that are not used by this value
    let digitsToHide = Math.max(0, unusedDigitPositions);

    // Iterate through digits from most significant to least significant
    for (
      let digitIndex = this.digits.length - 1;
      digitIndex >= 0;
      digitIndex--
    ) {
      if (digitsToHide > 0) {
        // Hide this digit as it's a leading zero
        this.digits[digitIndex].visible = false;
        digitsToHide--;
      } else {
        this.digits[digitIndex].visible = true;
        const actualDigitIndex = this.digits.length - 1 - digitIndex;
        const digitPositionInValue = actualDigitIndex - unusedDigitPositions;

        if (
          digitPositionInValue >= 0 &&
          digitPositionInValue < formattedDigits.length
        ) {
          const isZeroDigit = formattedDigits[digitPositionInValue] === "0";
          const isLeastSignificantDigit = digitIndex === 0; // digitIndex 0 is LSD (rightmost)
          const isMostSignificantDigit = digitPositionInValue === 0; // First digit in formatted value

          this.digits[digitIndex].zeroVisible =
            isLeastSignificantDigit || (isZeroDigit && !isMostSignificantDigit);
        } else {
          this.digits[digitIndex].zeroVisible = false;
        }
      }
    }

    // Also determine visibility for symbols based on their position
    this.determineSymbolVisibility(value, formattedValue, unusedDigitPositions);

    // Reposition elements to avoid gaps
    this.repositionElements();

    this.applySizeConstraints();
  }

  private determineSymbolVisibility(
    value: number,
    formattedValue: string,
    unusedDigitPositions: number,
  ): void {
    // Simple and elegant solution: Count delimiters in the actual value
    // and show the rightmost delimiters to match

    // Count all non-digit characters in the formatted value
    const valueSymbols = formattedValue.split("").filter((c) => !/\d/.test(c));
    const delimiterCount = valueSymbols.filter(
      (c) => c === "," || c === ".",
    ).length;

    // Strategy: Show the rightmost delimiters, hide the leftmost ones
    // This matches how number formatting works (delimiters added from the right)

    // First, hide all symbols
    this.symbols.forEach((symbol) => {
      symbol.visible = false;
    });

    // Then show the rightmost delimiters (starting from the end)
    // Also always show the first symbol (currency symbol)
    if (this.symbols.length > 0) {
      // Always show the first symbol (currency symbol)
      this.symbols[0].visible = true;

      // Show delimiters from the right
      let delimitersShown = 0;
      for (let i = this.symbols.length - 1; i > 0; i--) {
        if (delimitersShown < delimiterCount) {
          this.symbols[i].visible = true;
          delimitersShown++;
        }
      }
    }

    this.applySizeConstraints();
  }

  private repositionElements(): void {
    // Reposition elements to avoid gaps when visibility changes
    let currentX = 0;
    let digitIndex = this.digits.length - 1; // Start from the most significant digit (leftmost)
    let symbolIndex = 0;

    for (let i = 0; i < this.formattedExample.length; i++) {
      const char = this.formattedExample[i];
      if (/\d/.test(char)) {
        // Reposition digit if it is visible
        if (digitIndex >= 0 && this.digits[digitIndex].visible) {
          this.digits[digitIndex].x = currentX;
          currentX += this.digits[digitIndex].getDigitWidth();
        }
        digitIndex--;

        // Add spacing after digits
        currentX += this._spacing;
      } else {
        // Skip grouped non-numeric characters
        let symbolText = char;
        let j = i + 1;
        while (
          j < this.formattedExample.length &&
          !/\d/.test(this.formattedExample[j])
        ) {
          symbolText += this.formattedExample[j];
          j++;
        }

        // Reposition symbol if it is visible
        if (
          symbolIndex < this.symbols.length &&
          this.symbols[symbolIndex].visible
        ) {
          this.symbols[symbolIndex].x = currentX;
          this.symbols[symbolIndex].y = 0;
          currentX += this.symbols[symbolIndex].getSymbolWidth();
        }
        symbolIndex++;

        // Add spacing after delimiters (comma, period) but not after other symbols like currency
        if (symbolText === "," || symbolText === ".") {
          currentX += this._spacing;
        }

        i = j - 1; // Skip the processed characters
      }
    }
  }

  private applySizeConstraints(): void {
    // Get the natural bounds of the content
    const contentWidth = this.getOdometerWidth();
    const contentHeight = this.getOdometerHeight();

    // Calculate scaling factors
    let scaleX = 1;
    let scaleY = 1;
    let needsScaling = false;

    if (this._maxWidth !== undefined && contentWidth > this._maxWidth) {
      scaleX = this._maxWidth / contentWidth;
      needsScaling = true;
    }

    if (this._maxHeight !== undefined && contentHeight > this._maxHeight) {
      scaleY = this._maxHeight / contentHeight;
      needsScaling = true;
    }

    // Use the smaller scale factor to maintain aspect ratio
    const finalScale = Math.min(scaleX, scaleY);

    // Apply the scaling
    this.contentContainer.scale.set(finalScale, finalScale);

    // Calculate positioned content dimensions
    const scaledWidth = contentWidth * finalScale;
    const scaledHeight = contentHeight * finalScale;

    // Position based on content anchor points
    const containerWidth = this._maxWidth ?? scaledWidth;
    const containerHeight = this._maxHeight ?? scaledHeight;

    // Calculate position based on anchor (center by default)
    const posX = -scaledWidth * this._contentAnchor.x;
    const posY = -scaledHeight * this._contentAnchor.y;

    this.contentContainer.x = posX;
    this.contentContainer.y = posY;

    // this.dbgGfx
    //   .clear()
    //   .rect(posX, posY, this._maxWidth ?? 100, this._maxHeight ?? 100)
    //   .fill({
    //     color: "0xff0000",
    //     alpha: 0.2,
    //   });
    // this.addChild(this.dbgGfx);
    // this.dbgGfx
    //   .clear()
    //   .rect(
    //     this.contentContainer.x,
    //     this.contentContainer.y,
    //     scaledWidth,
    //     scaledHeight,
    //   )
    //   .fill("0x00ff00cc")
    //   .rect(posX - 1, posY - 20, 2, 40)
    //   .fill({ color: 0xffff00 })
    //   .rect(posX - 20, posY - 1, 40, 2)
    //   .fill({ color: 0xffff00 });

    // console.log(
    //   `CONTENT calculatedWidth=${contentWidth} containerWidth=${this.contentContainer.width}`,
    // );
  }

  private determineVisibilityForAnimation(value: number): void {
    const formattedValue = this.formatter(value);

    // Extract digits from the formatted value
    const formattedDigits = formattedValue
      .split("")
      .filter((char) => /\d/.test(char));

    // Calculate how many digit positions in the odometer are NOT used by this value
    const unusedDigitPositions = this.digits.length - formattedDigits.length;

    // For animations, show ALL digits including zeros
    let digitsToHide = Math.max(0, unusedDigitPositions);

    // Iterate through digits from most significant to least significant
    for (
      let digitIndex = this.digits.length - 1;
      digitIndex >= 0;
      digitIndex--
    ) {
      if (digitsToHide > 0) {
        // Hide this digit as it's a leading zero position not used by this value
        this.digits[digitIndex].visible = false;
        digitsToHide--;
      } else {
        this.digits[digitIndex].visible = true;
        // For animations, always show zeros to ensure proper spinning effect
        this.digits[digitIndex].zeroVisible = true;
      }
    }

    // Also determine visibility for symbols based on their position
    const valueSymbols = formattedValue.split("").filter((c) => !/\d/.test(c));
    const delimiterCount = valueSymbols.filter(
      (c) => c === "," || c === ".",
    ).length;

    // Strategy: Show the rightmost delimiters, hide the leftmost ones
    // First, hide all symbols
    this.symbols.forEach((symbol) => {
      symbol.visible = false;
    });

    // Then show the rightmost delimiters (starting from the end)
    // Also always show the first symbol (currency symbol)
    if (this.symbols.length > 0) {
      // Always show the first symbol (currency symbol)
      this.symbols[0].visible = true;

      // Show delimiters from the right
      let delimitersShown = 0;
      for (let i = this.symbols.length - 1; i > 0; i--) {
        if (delimitersShown < delimiterCount) {
          this.symbols[i].visible = true;
          delimitersShown++;
        }
      }
    }

    // Reposition elements to avoid gaps
    this.repositionElements();

    this.applySizeConstraints();
  }

  private extractDigits(value: number): number[] {
    const formattedValue = this.formatter(value);
    return formattedValue
      .split("")
      .map((char) => {
        // If the character is a digit, return its numeric value; otherwise, return -1 (or another placeholder)
        return /\d/.test(char) ? parseInt(char, 10) : -1;
      })
      .filter((digit) => digit !== -1);
  }

  public rotateTo(
    value: number,
    options: {
      duration: number;
      easing?: gsap.EaseFunction | gsap.EaseString;
      animate?: boolean;
    } = { duration: 1, easing: "none" },
  ): void {
    this.axleTween?.kill();
    this.axleTween = null;
    this.digits.forEach((col) => col.killActiveTween());

    if (options.animate === undefined) options.animate = true;

    const targetPos = Math.round(
      Math.max(0, value) * Math.pow(10, this.fractionDigitCount),
    );
    const divisors = this.digits.map((_, i) => Math.pow(10, i));

    this.axleTween = gsap.to(this.axleProxy, {
      pos: targetPos,
      duration: options.duration,
      ease: options.easing,
      onUpdate: (animate: boolean) => {
        const g = this.axleProxy.pos;
        for (let i = 0; i < this.digits.length; i++) {
          const pos = g / divisors[i];
          const base = Math.floor(pos);

          // If animation disabled just set the number.
          if (!animate) {
            this.digits[i].setScrollPosition(base);
            continue;
          }

          if (i === 0) {
            this.digits[i].setScrollPosition(pos);
            continue;
          }

          const frac = pos - base;
          const start = 1 - Math.pow(10, -i);
          const span = 1 - start;
          this.digits[i].setScrollPosition(
            base + (frac <= start ? 0 : Math.min(1, (frac - start) / span)),
          );
        }

        this.determineVisibility(
          this.axleProxy.pos / Math.pow(10, this.fractionDigitCount),
        );
      },
      onUpdateParams: [options.animate],
      onComplete: () => {
        this._currentValue = value;
        this.lastDigits = this.extractDigits(value);
        this.axleTween = null;
      },
    });
  }

  public fastSpin(
    value: number,
    options: {
      duration: number;
      extraSpins?: number;
      easing?: gsap.EaseFunction | gsap.EaseString;
    },
  ): void {
    console.log("Fast spin duration:", options.duration);
    this.axleTween?.kill();
    this.axleTween = null;
    this.digits.forEach((col) => col.killActiveTween());

    if (options.extraSpins === undefined) options.extraSpins = 0;
    if (options.easing === undefined) options.easing = "none";

    const targetPos = Math.round(
      Math.max(0, value) * Math.pow(10, this.fractionDigitCount),
    );

    // Get current and target digits
    const currentDigits = this.extractDigits(this._currentValue);
    const targetDigits = this.extractDigits(value);

    // Animate each digit individually using rollTo
    const extraSpins = options.extraSpins ?? this._extraSpins;

    // For fastSpin, use animation visibility to show all zeros during spinning
    this.determineVisibilityForAnimation(value);

    // Animate the axle position to match rotateTo behavior
    this.axleTween = gsap.to(this.axleProxy, {
      pos: targetPos,
      duration: options.duration,
      ease: options.easing ?? "none",
      onUpdate: () => {
        // Keep visibility consistent during fastSpin animation
        this.determineVisibilityForAnimation(value);
      },
      onComplete: () => {
        this._currentValue = value;
        this.lastDigits = targetDigits;
        this.axleTween = null;
      },
    });

    for (let i = 0; i < this.digits.length; i++) {
      const targetDigitIndex = targetDigits.length - 1 - i;
      const currentDigitIndex = currentDigits.length - 1 - i;

      const currentDigit =
        currentDigitIndex >= 0 ? currentDigits[currentDigitIndex] : 0;
      const targetDigit =
        targetDigitIndex >= 0 ? targetDigits[targetDigitIndex] : 0;

      // Add extra spin to least significant digit when spinning more than 19
      // const digitExtraSpins = 0;
      const digitExtraSpins =
        i === 0 &&
        Math.abs(value - this._currentValue) > 19 &&
        (options.duration > 1.5 || targetDigit - this.digits[i].getValue() < 5)
          ? extraSpins + 1
          : extraSpins;

      // console.log(
      //   targetDigit,
      //   this.digits[i].getValue(),
      //   targetDigit - this.digits[i].getValue() < 5,
      // );

      if (this.digits[i].visible) {
        // this.digits[i].snapTo(currentDigit);
        this.digits[i].rollTo(targetDigit, {
          duration: options.duration,
          extraSpins: digitExtraSpins,
          easing: options.easing,
        });
      }
    }
  }

  /**
   * Test method to demonstrate fastSpin functionality
   * This can be called to verify that each digit spins independently
   */
  public testFastSpin(): void {
    console.log("Testing fastSpin method...");

    // Test spinning from current value to a different value
    const testValues = [123, 456, 789, 1000, 500, 0];
    let currentIndex = 0;

    const testNextValue = () => {
      const targetValue = testValues[currentIndex];
      currentIndex = (currentIndex + 1) % testValues.length;

      console.log(`Spinning to: ${targetValue}`);
      this.fastSpin(targetValue, {
        duration: 3,
        extraSpins: 3,
      });

      // Schedule next test after animation completes
      setTimeout(testNextValue, 3000);
    };

    // Start the test
    testNextValue();
  }

  public getOdometerWidth(): number {
    if (this.digits.length === 0 && this.symbols.length === 0) return 0;

    // Calculate total width by exactly simulating the positioning logic
    let currentX = 0;
    let digitIndex = this.digits.length - 1; // Start from most significant digit (leftmost)
    let symbolIndex = 0;
    let maxPosition = 0;

    for (let i = 0; i < this.formattedExample.length; i++) {
      const char = this.formattedExample[i];
      if (/\d/.test(char)) {
        // Position digit if visible
        if (digitIndex >= 0 && this.digits[digitIndex].visible) {
          // The actual position of this digit
          const digitPos = currentX;
          // Update max position to be the end of this digit
          maxPosition = Math.max(
            maxPosition,
            digitPos + this.digits[digitIndex].getDigitWidth(),
          );
          currentX += this.digits[digitIndex].getDigitWidth();
        }
        digitIndex--;

        // Add spacing after digits
        currentX += this._spacing;
      } else {
        // Position symbol if visible
        if (
          symbolIndex < this.symbols.length &&
          this.symbols[symbolIndex].visible
        ) {
          // The actual position of this symbol
          const symbolPos = currentX;
          // Update max position to be the end of this symbol
          maxPosition = Math.max(
            maxPosition,
            symbolPos + this.symbols[symbolIndex].getSymbolWidth(),
          );
          currentX += this.symbols[symbolIndex].getSymbolWidth();
        }
        symbolIndex++;

        // Add spacing after delimiters (comma, period) but not after other symbols like currency
        if (char === "," || char === ".") {
          currentX += this._spacing;
        }
      }
    }

    return maxPosition;
  }

  public getOdometerHeight(): number {
    // Return the maximum height between digits and symbols
    const digitHeight = this.digits[0]?.getDigitHeight() ?? 0;
    const symbolHeight = this.symbols[0]?.getSymbolHeight() ?? 0;
    return Math.max(digitHeight, symbolHeight);
  }

  /**
   * The content anchor point that controls positioning within maxWidth/maxHeight constraints.
   * This is separate from PIXI's anchor property.
   * Can be a number (same value for x/y) or an object with x and y properties.
   * - (0,0) is top-left
   * - (0.5,0.5) is center
   * - (1,1) is bottom-right
   * @default { x: 0, y: 0 }
   */
  get contentAnchor(): ObservablePoint {
    return this._contentAnchor;
  }

  set contentAnchor(value: number | { x: number; y: number } | undefined) {
    // Check if content anchor is initialized (might be called during construction)
    if (!this._contentAnchor) {
      return;
    }

    // Store current values to check if they actually change
    const currentX = this._contentAnchor.x;
    const currentY = this._contentAnchor.y;

    if (value === undefined) {
      // If undefined, keep current values
      return;
    }

    if (typeof value === "number") {
      this._contentAnchor.set(value, value);
    } else {
      this._contentAnchor.set(value.x ?? 0, value.y ?? 0);
    }

    // Only apply constraints if the anchor actually changed
    if (
      this._contentAnchor.x !== currentX ||
      this._contentAnchor.y !== currentY
    ) {
      this.applySizeConstraints();
    }
  }
}
